import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

// バックエンドからの共通レスポンス型
interface ApiResponse<T> {
  status: boolean;
  statusCode: number;
  message: string;
  data: T;
}

@Injectable({
  providedIn: 'root'
})
export class AscReportService {
  private apiUrl = this.getApiUrl();

  constructor(private http: HttpClient) { }

  private getApiUrl(): string {
    const path = '/admin/ascreport';
    const isUnregistered = environment.unregisteredAdminPaths?.includes(path);

    // ローカル開発環境（localhost）の場合
    if (environment.apiUrl.includes('localhost')) {
      return `${environment.apiUrl}/AscReport`;
    }

    if (environment.useApiGateway && !isUnregistered) {
      return `${environment.apiGatewayUrl}/ascreport`;  // APIM登録済みパス
    } else {
      return `${environment.directApiUrl}/AscReport`;   // 直接接続
    }
  }

  getAscReportData(request: any): Observable<any> {
    const params = new URLSearchParams();
    params.append('yearMonth', request.yearMonth);
    params.append('closeDate', request.closeDate);
    if (request.customerCode) params.append('customerCode', request.customerCode);
    if (request.clientId) params.append('clientId', request.clientId.toString());
    if (request.branchNumbers) params.append('branchNumbers', request.branchNumbers);
    params.append('andCondition', request.andCondition);
    params.append('displayOption', request.displayOption);
    params.append('outputOption', request.outputOption);
    params.append('page', request.page.toString());
    params.append('pageSize', request.pageSize.toString());
    
    return this.http.get<any>(`${this.apiUrl}/data?${params.toString()}`);
  }

  // CSV全件エクスポート（APIMに登録済みの /data エンドポイントを pageSize=100000 で呼び出す）
  exportAllAscReportData(request: any): Observable<any> {
    const params = new URLSearchParams();
    params.append('yearMonth', request.yearMonth);
    params.append('closeDate', request.closeDate);
    if (request.customerCode) params.append('customerCode', request.customerCode);
    if (request.clientId) params.append('clientId', request.clientId.toString());
    if (request.branchNumbers) params.append('branchNumbers', request.branchNumbers);
    params.append('andCondition', request.andCondition);
    params.append('displayOption', request.displayOption);
    params.append('outputOption', request.outputOption);
    params.append('page', '1');
    params.append('pageSize', '100000');

    return this.http.get<any>(`${this.apiUrl}/data?${params.toString()}`);
  }

  getDisplayColumns(andCondition: string, displayOption: string, outputOption: string = 'summary'): string[] {
    // 詳細モードの場合（欠勤/有休/在宅条件のみ）
    if (outputOption === 'detail' &&
        (andCondition === 'absence' || andCondition === 'paidHoliday' || andCondition === 'teleWork')) {
      return [
        'customerName', 'customerOfficeName', 'staffCode', 'staffName',
        'jobOrderLink', 'timesheetLink', 'customerLink', 'ascLink',
        'dayDate'
      ];
    }

    if (displayOption === 'allItems') {
      const columns = [
        'customerName', 'customerOfficeName', 'staffCode', 'staffName',
        'jobOrderId', 'timesheetId', 'customerOfficeId', 'ascUq',
        'workTime', 'overWorkTime', 'midnightInWorkTime', 'midnightOverWorkTime', 'total',
        'editing', 'submitted', 'denied', 'approved',
        'attendance', 'absence', 'paidHoliday', 'teleWork', 'overtimeDays', 'holidayWork'
      ];
      
      // 未報告/否認ありの場合はASCリマインド列を追加
      if (andCondition === 'unreported') {
        columns.push('ascRemind');
      }
      // 未承認ありの場合は顧客リマインド列を追加
      else if (andCondition === 'unapproved') {
        columns.push('cusRemind');
      }
      
      return columns;
    } else {
      // 未報告/否認あり・未承認ありは新レイアウト（顧客名・部署名・■リンク）
      if (andCondition === 'unreported') {
        return [
          'checkbox', 'customerName', 'customerOfficeName', 'staffCode', 'staffName',
          'jobOrderLink', 'timesheetLink', 'customerLink', 'ascLink',
          'editing', 'submitted', 'denied', 'approved', 'ascRemind'
        ];
      }
      if (andCondition === 'unapproved') {
        return [
          'checkbox', 'customerName', 'customerOfficeName', 'staffCode', 'staffName',
          'jobOrderLink', 'timesheetLink', 'customerLink', 'ascLink',
          'editing', 'submitted', 'denied', 'approved', 'cusRemind'
        ];
      }

      const columns = [
        'customerName', 'customerOfficeName', 'staffCode', 'staffName',
        'jobOrderLink', 'timesheetLink', 'customerLink', 'ascLink'
      ];

      if (andCondition !== 'none') {
        if (andCondition === 'overtime') columns.push('overtimeDays');
        if (andCondition === 'holiday') columns.push('holidayWork');
        if (andCondition === 'absence') columns.push('absence');
        if (andCondition === 'paidHoliday') columns.push('paidHoliday');
        if (andCondition === 'teleWork') columns.push('teleWork');
      }

      if (andCondition === 'comments') {
        columns.push('comments');
      }

      return columns;
    }
  }

  getReminderEmails(request: ReminderEmailRequest): Observable<ApiResponse<ReminderEmailResponse>> {
    return this.http.post<ApiResponse<ReminderEmailResponse>>(`${this.apiUrl}/reminder-emails`, request);
  }

  getReminderMailInfo(request: ReminderEmailRequest): Observable<ApiResponse<ReminderMailResponse>> {
    return this.http.post<ApiResponse<ReminderMailResponse>>(`${this.apiUrl}/reminder-mail-info`, request);
  }

  sendReminderMails(request: ReminderEmailRequest): Observable<ApiResponse<SendReminderResponse>> {
    return this.http.post<ApiResponse<SendReminderResponse>>(`${this.apiUrl}/send-reminder-mails`, request);
  }

  validateToken(token: string): Observable<TokenValidationResponse> {
    return this.http.get<TokenValidationResponse>(`${this.apiUrl}/validate-token?token=${encodeURIComponent(token)}`);
  }

  getResourceData(resourceType: string, id: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/resource-data/${resourceType}/${id}`);
  }

  // 勤務期間アラート関連
  getUnprocessedWorkPeriodAlerts(pageNo: number, pageSize: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/work-period-alerts/unprocessed?pageNo=${pageNo}&pageSize=${pageSize}`);
  }

  getProcessedWorkPeriodAlerts(period: string, pageNo: number, pageSize: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/work-period-alerts/processed?period=${period}&pageNo=${pageNo}&pageSize=${pageSize}`);
  }

  saveProcComment(request: SaveProcCommentRequest): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/work-period-alerts/save-comment`, request);
  }

  updateBPchk(request: UpdateBPchkRequest): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/work-period-alerts/update-bpchk`, request);
  }

  executeAlternativeProcess(request: ExecuteAlternativeProcessRequest): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/work-period-alerts/execute-alternative`, request);
  }

  completeProcess(request: CompleteProcessRequest): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/work-period-alerts/complete`, request);
  }

  rejectProcess(request: RejectProcessRequest): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/work-period-alerts/reject`, request);
  }

  // その他のアラートリスト関連
  getOtherAlerts(period: string, status: string, pageNo: number, pageSize: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/other-alerts?period=${period}&status=${status}&pageNo=${pageNo}&pageSize=${pageSize}`);
  }

  deleteTimesheetForAlert(request: DeleteTimesheetAlertRequest): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/work-period-alerts/delete-timesheet`, request);
  }

  // 転記処理データ取得
  getMoveTransferData(odchgId: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/move-transfer/${odchgId}`);
  }

  // 削除データ検索（RequestID/OdchgID/JobOrderIDで検索）
  searchDeletedData(requestId?: string, odchgId?: number, jobOrderId?: number): Observable<any> {
    const params = new URLSearchParams();
    if (requestId) params.append('requestId', requestId);
    if (odchgId) params.append('odchgId', odchgId.toString());
    if (jobOrderId) params.append('jobOrderId', jobOrderId.toString());
    
    return this.http.get<any>(`${this.apiUrl}/search-deleted-data?${params.toString()}`);
  }

  getDestinationTimesheetData(sourceJobOrderId: number, destinationJobOrderId: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/destination-timesheet/${sourceJobOrderId}/${destinationJobOrderId}`);
  }

  completeMoveOrDelete(request: CompleteMoveOrDeleteRequest): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/complete-move-or-delete`, request);
  }

  moveToDestination(request: MoveToDestinationRequest): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/move-to-destination`, request);
  }

  deleteFromSource(request: DeleteFromSourceRequest): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/delete-from-source`, request);
  }

  confirmProcess(request: ConfirmProcessRequest): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/confirm-process`, request);
  }

  // 顧客一覧取得（顧客一覧作成ボタン用）
  getCustomerList(yearMonth: string, branchNumbers: string): Observable<any> {
    const params = new URLSearchParams();
    params.append('yearMonth', yearMonth);
    if (branchNumbers) params.append('branchNumbers', branchNumbers);
    return this.http.get<any>(`${this.apiUrl}/customer-list?${params.toString()}`);
  }
}

export interface CompleteMoveOrDeleteRequest {
  ODchgID: number;
  OldJobOrderID: number;
  NewJobOrderID: number;
  SelectedLineItemIDs: number[];
  SelectedCommentTimesheetIDs: number[];
}

export interface MoveToDestinationRequest {
  ODchgID?: number | null;
  RequestID?: number | null;
  SourceJobOrderID?: number | null;
  NewJobOrderID: number;
  SelectedLineItemIDs: number[];
  SelectedCommentTimesheetIDs: number[];
  CurrentUserID: number;
}

export interface DeleteFromSourceRequest {
  ODchgID: number;
  SelectedCommentTimesheetIDs: number[];
}

export interface ConfirmProcessRequest {
  RequestID?: string;
  ODchgID?: number;
}

export interface TokenValidationResponse {
  isValid: boolean;
  tabId?: string;
  showMoveTs: boolean;
  dataID?: number;
  resourceType?: string;
  keyColumn?: string;
  errorMessage?: string;
}

export interface ReminderEmailRequest {
  yearMonth: string;
  closingDay: string;  // バックエンドのClosingDayに対応
  andCondition: string;
  timesheetIds: string[];
  jobOrderIds: string[];
  userIds: string[];
  mailInfos?: ReminderMailInfo[];  // /reminder-mail-info 取得済みの情報。設定時は再取得をスキップ
}

export interface ReminderEmailResponse {
  emailAddresses: string[];
  totalCount: number;
  approverTargets: { [approverEmail: string]: string[] };
  approverUserIDs: { [approverEmail: string]: string };
}

export interface ReminderMailInfo {
  title: string;
  body: string;
  sender: string;
  recipients: string[];
  templateID: number;
  receiverUserIDs: string[];
  timesheetIDs: string[];
}

export interface ReminderMailResponse {
  mailInfos: ReminderMailInfo[];
  totalCount: number;
}

export interface SendReminderResponse {
  sentCount: number;
  totalCount: number;
  errors: string[];
  message: string;
}

// 勤務期間アラート関連の型定義
export interface WorkPeriodAlert {
  odchgID: number;
  jobOrderID: number;
  staffID?: number;
  userCode?: string;
  staffName?: string;
  customerOfficeID?: number;
  customerOfficeCode?: string;
  customerName?: string;
  oldStartDate?: string;
  newStartDate?: string;
  oldEndDate?: string;
  newEndDate?: string;
  dtStartDate?: string;
  dtEndDate?: string;
  createDate?: string;
  dataState?: string;
  alertSentTo?: string;
  alertSentDate?: string;
  bpchk: number;
  procState?: string;
  procStatus?: string;
  status?: string;
  prePay?: string;
  repName?: string;
  repEmail?: string;
  procUserName?: string;
  procComment?: string;
  procDate?: string;
  sendDate?: string;
}

export interface SaveProcCommentRequest {
  odchgID: number;
  procComment: string;
  bpchk: number;
  userID: number;
}

export interface UpdateBPchkRequest {
  odchgID: number;
  userID: number;
}

export interface ExecuteAlternativeProcessRequest {
  odchgID: number;
  userID: number;
}

export interface CompleteProcessRequest {
  odchgID: number;
  userID: number;
  procStatus?: string;
}

export interface RejectProcessRequest {
  odchgID: number;
  userID: number;
}

// 転記処理関連
export interface MoveTransferSummary {
  jobOrderID: number;
  staffID: number;
  userCode: string;
  userName: string;
  originalPeriod: string;
  newPeriod: string;
}

export interface TransferDestination {
  jobOrderID: number;
  oldestDayDate: string;
  latestDayDate: string;
  resultText: string;
}

export interface TimesheetLineItem {
  timesheetLineItemID: number;
  timesheetID: number;
  state: string;
  dayDate: string;
  startTime: string;
  endTime: string;
  breakMinutes: number | null;
  midnightBreakMinutes: number | null;
  teleWork: string;
  lineItem: string;
  costItem: string;
  cost: number | null;
}

export interface TimesheetComments {
  timesheetID: number;
  comments: string;
  complaintComments: string;
  trainingComments: string;
}

export interface SourceTimesheetData {
  customerOfficeCode: string;
  lineItems: TimesheetLineItem[];
  comments: TimesheetComments;
}

export interface DestinationLineItem {
  timesheetLineItemID: number;
  timesheetID: number;
  state: string;
  dayDate: string;
  startTime: string;
  endTime: string;
  breakMinutes: number | null;
  midnightBreakMinutes: number | null;
  teleWork: string;
  lineItem: string;
  costItem: string;
  cost: number | null;
}

export interface DestinationComments {
  timesheetID: number;
  comments: string;
  complaintComments: string;
  trainingComments: string;
}

export interface DestinationTimesheetData {
  jobOrderID: number;
  customerOfficeCode: string;
  oldestDayDate: string;
  latestDayDate: string;
  lineItems: DestinationLineItem[];
  comments: DestinationComments[];
}

export interface MoveTransferResponse {
  summary: MoveTransferSummary;
  destinations: TransferDestination[];
  sourceData: SourceTimesheetData;
  destinationData: DestinationTimesheetData;
}

export interface DeleteTimesheetAlertRequest {
  odchgID: number;
  procUserID: number;
  editStartDate?: string;
  editEndDate?: string;
}

export interface DeleteTimesheetAlertResult {
  isSuccess: boolean;
  odchgID: number;
  repEmail: string;
  templateName: string;
  changeTypeText: string;
  jobOrderID: string;
  clientInfo: string;
  ascInfo: string;
  oldPeriod: string;
  newPeriod: string;
}

